import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import type { Tone } from "./status";

const VALUE_TONE: Record<Tone, string> = {
  ok: "text-foreground",
  warn: "text-warn",
  danger: "text-danger",
  muted: "text-muted-foreground",
};

/**
 * One number, its name, and a line saying what the number means.
 *
 * The caption is not decoration: every figure on this dashboard has at least
 * one reading that looks alarming and is not, and the caption is where that
 * gets said instead of being learned the hard way.
 */
export function Stat({
  label,
  value,
  caption,
  tone = "ok",
  className,
  ...rest
}: {
  label: string;
  value: ReactNode;
  caption?: ReactNode;
  tone?: Tone;
  className?: string | undefined;
} & { "data-level"?: string; "data-testid"?: string }) {
  return (
    <div
      className={cn("flex flex-col gap-1 rounded-lg border border-border bg-card px-4 py-3", className)}
      {...rest}
    >
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span data-numeric className={cn("text-3xl font-semibold leading-none", VALUE_TONE[tone])}>
        {value}
      </span>
      {caption !== undefined ? (
        <span className="text-xs leading-snug text-muted-foreground">{caption}</span>
      ) : null}
    </div>
  );
}
